import clsx from 'clsx';
import React, { useMemo } from 'react';
import {
  ApiFilterOperation,
  AutocompleteSource,
  DomainObject,
  FilterComponentProps,
  useEventCallback,
} from '@eas/common-web';
import { FilterAutocompleteCell } from '@composite/filter/filter-cells/filter-autocomplete-cell';
import { autocompleteLabelMapper } from '@components/form/misc/label-mappers';
import { Operation, useNullableAutocompleteOperations } from '../filter-api';
import { useStyles } from '../filter-styles';
import { NullableFilterCell } from './nullable-filter-cell';

export function NullableAutocompleteFilterCell<TYPE extends DomainObject>(
  dataHook: () => AutocompleteSource<TYPE>,
  labelMapper?: (option: TYPE) => string,
  multiple = false
) {
  return useMemo(
    () =>
      function Cell({
        state,
        onChangeState,
        disabled,
        ...rest
      }: FilterComponentProps) {
        const classes = useStyles();

        const operations = useNullableAutocompleteOperations();
        const nullableOperation =
          state.operation === ApiFilterOperation.NOT
            ? ApiFilterOperation.OR
            : state.operation;

        const handleOperationChange = useEventCallback(
          (operation: string | string[] | Operation | Operation[] | null) => {
            if (!operation || Array.isArray(operation)) {
              return;
            }

            const operationId =
              typeof operation === 'string' ? operation : operation.id;

            if (
              [
                ApiFilterOperation.IS_NULL,
                ApiFilterOperation.NOT_NULL,
              ].includes(operationId as ApiFilterOperation)
            ) {
              onChangeState({
                ...state,
                operation: operationId as ApiFilterOperation,
                value: null,
                filters: undefined,
                object: undefined,
              });
            } else {
              onChangeState({
                ...state,
                operation: ApiFilterOperation.OR,
              });
            }
          }
        );

        const AutocompleteCell = FilterAutocompleteCell(
          dataHook,
          autocompleteLabelMapper,
          multiple
        );

        return (
          <div
            className={clsx(classes.root, classes.columnDirection, {
              [classes.disabled]: disabled,
            })}
          >
            <NullableFilterCell
              source={operations}
              value={nullableOperation}
              onChange={handleOperationChange}
              addMargin={
                ![
                  ApiFilterOperation.NOT_NULL,
                  ApiFilterOperation.IS_NULL,
                ].includes(state.operation)
              }
            />
            {[ApiFilterOperation.OR, ApiFilterOperation.NOT].includes(
              state.operation
            ) && (
              <AutocompleteCell
                {...rest}
                state={state}
                onChangeState={onChangeState}
                disabled={disabled}
              />
            )}
          </div>
        );
      },
    [dataHook]
  );
}
